import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import Button from '../../../../src/components/styledComponents/Button';
import { getDriversForPolicy } from '../../../api/getDriversForPolicy';
import Select from '../../../components/styledComponents/Select';
import colors from '../../../constants/colors';
import useAnalytics from '../../../hooks/useAnalytics';
import { useGlassClaimForm } from './GlassClaim';
import { GlassClaimForm, policySelectionFormValidation } from './validations';

const GlassClaim = () => {
  const queryClient = useQueryClient();
  const {
    formValues,
    setFormValues,
    policies: autoPolicies,
  } = useGlassClaimForm() || {};
  const { track } = useAnalytics();

  const navigate = useNavigate();

  const policyOptions =
    autoPolicies?.map((policy) => ({
      value: policy.policyNumber,
      display: `${policy.policyType} policy ${policy.policyNumber}`,
    })) ?? [];

  const prefetchDriversForPolicy = async (policyNumber: string) => {
    await queryClient.prefetchQuery({
      queryKey: ['drivers', policyNumber],
      queryFn: () => getDriversForPolicy(policyNumber),
    });
  };

  const policySelectionFormProps = useForm<GlassClaimForm>({
    defaultValues: {
      policyNumber:
        formValues?.policyNumber ?? autoPolicies?.[0]?.policyNumber ?? '',
    },
    mode: 'onChange',
    resolver: yupResolver(policySelectionFormValidation),
  });

  const { control, getValues } = policySelectionFormProps;

  const handleContinueClick = () => {
    const values = getValues();
    const policySelected = autoPolicies.find(
      (policy) => policy.policyNumber === values.policyNumber
    );
    setFormValues({ ...formValues, ...values });
    track('Claim Policy Entered', {
      event_type: 'User Information Entered',
      location: 'Feature Card',
      claim_type: 'glass',
      policy_number: values.policyNumber,
      policy_type: policySelected?.policyType,
      state: policySelected?.riskState,
    });
    navigate('/claims/glass/details');
  };

  const handleCancelClick = () => {
    navigate(-1);
  };

  return (
    <Stack>
      <Typography
        sx={{
          fontSize: { mobile: 16, desktop: 18 },
          fontWeight: 400,
          fontFamily: 'Cabin',
          mb: 2,
          lineHeight: '24px',
          width: 1,
        }}
      >
        Which policy do you want to use for this claim?
      </Typography>
      <Controller
        name="policyNumber"
        control={control}
        render={({ field: { value, onChange } }) => (
          <Select
            dataTestId="policySelect"
            options={policyOptions}
            value={value}
            onChange={(e) => {
              prefetchDriversForPolicy(e.target.value);
              setFormValues({ ...formValues, policyNumber: e.target.value });
              onChange(e);
            }}
            label="Select a policy"
            ariaControlsValue="policyNumberMenu"
            hideLabel
          />
        )}
      />
      <Stack
        direction={{ mobile: 'column', tablet: 'row' }}
        spacing={{
          mobile: 1,
          tablet: 2,
        }}
        sx={{
          justifyContent: "flex-end",
          mt: 3
        }}>
        <Button
          data-testid="glassClaimVehicleCancel"
          variant="outlined"
          color="inherit"
          sx={{
            width: { mobile: 1, tablet: 'auto' },
            borderWidth: 2,
            fontWeight: 700,
            textTransform: 'none',
            fontSize: { mobile: 16, tablet: 18 },
            color: colors.csaaBlack,
            px: { mobile: 4, desktop: 3 },
            wordWrap: 'nowrap',
            minWidth: 'max-content',
          }}
          onClick={handleCancelClick}
        >
          Cancel
        </Button>
        <Button
          data-testid="glassClaimVehicleContinue"
          variant="contained"
          color="primary"
          disabled={!policySelectionFormProps.formState.isValid}
          sx={{
            width: { mobile: 1, tablet: 'auto' },
            borderWidth: 2,
            fontWeight: 700,
            textTransform: 'none',
            fontSize: { mobile: 16, tablet: 18 },
            px: { mobile: 4, desktop: 3 },
            wordWrap: 'nowrap',
            minWidth: 'max-content',
          }}
          onClick={handleContinueClick}
        >
          Continue
        </Button>
      </Stack>
    </Stack>
  );
};

export default GlassClaim;
